import * as React from "react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../dialog/dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "../dialog/drawer";
import type { DialogContentProps } from "../dialog/types";
import { useMediaQuery } from "../hooks/use-media-query";

// Distributive Omit preserves discriminated unions (regular Omit collapses them).
type DistributiveOmit<T, K extends keyof any> = T extends any
  ? Omit<T, K>
  : never;

// Mobile = below the `sm` breakpoint (< 640px) — renders the Drawer arm.
function useIsMobileModal(): boolean {
  return !useMediaQuery("(min-width: 640px)");
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

type ModalContextValue = { isMobile: boolean };
const ModalContext = React.createContext<ModalContextValue | null>(null);

function useModalContext(): ModalContextValue {
  const ctx = React.useContext(ModalContext);
  if (!ctx) {
    throw new Error("Modal sub-components must be used inside <Modal>.");
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------

type ModalProps = {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  modal?: boolean;
  children?: React.ReactNode;
  /**
   * Props forwarded to the Drawer root when on mobile.
   * Use this for Vaul-specific options like `direction`, `snapPoints`, etc.
   */
  drawerProps?: DistributiveOmit<
    React.ComponentProps<typeof Drawer>,
    "open" | "defaultOpen" | "onOpenChange" | "modal" | "children"
  >;
};

function Modal({
  children,
  open,
  defaultOpen,
  onOpenChange,
  modal,
  drawerProps,
}: ModalProps) {
  const isMobile = useIsMobileModal();

  if (!isMobile) {
    return (
      <ModalContext.Provider value={{ isMobile: false }}>
        <Dialog
          open={open}
          defaultOpen={defaultOpen}
          onOpenChange={onOpenChange}
          modal={modal}
        >
          {children}
        </Dialog>
      </ModalContext.Provider>
    );
  }

  return (
    <ModalContext.Provider value={{ isMobile: true }}>
      <Drawer
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
        modal={modal}
        {...drawerProps}
      >
        {children}
      </Drawer>
    </ModalContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Trigger
// ---------------------------------------------------------------------------

function ModalTrigger(props: React.ComponentProps<typeof DialogTrigger>) {
  const { isMobile } = useModalContext();
  return isMobile ? <DrawerTrigger {...props} /> : <DialogTrigger {...props} />;
}

// ---------------------------------------------------------------------------
// Content
// ---------------------------------------------------------------------------

type ModalContentProps = {
  className?: string;
  children?: React.ReactNode;
  /**
   * Props forwarded to DialogContent when on desktop.
   * Use for `size`, `showCloseButton`, `showOverlay`, etc.
   */
  dialogProps?: DialogContentProps;
  /**
   * Props forwarded to DrawerContent when on mobile.
   */
  drawerProps?: Omit<
    React.ComponentProps<typeof DrawerContent>,
    "className" | "children"
  >;
};

function ModalContent({
  className,
  children,
  dialogProps,
  drawerProps,
}: ModalContentProps) {
  const { isMobile } = useModalContext();

  if (!isMobile) {
    return (
      <DialogContent className={className} {...dialogProps}>
        {children}
      </DialogContent>
    );
  }

  return (
    <DrawerContent className={className} {...drawerProps}>
      {children}
    </DrawerContent>
  );
}

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function ModalHeader(props: React.ComponentProps<"div">) {
  const { isMobile } = useModalContext();
  return isMobile ? (
    <DrawerHeader {...props} />
  ) : (
    <DialogHeader className="contents space-y-0" {...props} />
  );
}

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------

function ModalFooter(props: React.ComponentProps<"div">) {
  const { isMobile } = useModalContext();
  return isMobile ? <DrawerFooter {...props} /> : <DialogFooter {...props} />;
}

// ---------------------------------------------------------------------------
// Title
// ---------------------------------------------------------------------------

function ModalTitle(props: React.ComponentProps<typeof DialogTitle>) {
  const { isMobile } = useModalContext();
  return isMobile ? <DrawerTitle {...props} /> : <DialogTitle {...props} />;
}

// ---------------------------------------------------------------------------
// Description
// ---------------------------------------------------------------------------

function ModalDescription(
  props: React.ComponentProps<typeof DialogDescription>,
) {
  const { isMobile } = useModalContext();
  return isMobile ? (
    <DrawerDescription {...props} />
  ) : (
    <DialogDescription {...props} />
  );
}

// ---------------------------------------------------------------------------
// Close
// ---------------------------------------------------------------------------

function ModalClose({
  onMouseDown,
  ...props
}: React.ComponentProps<typeof DialogClose>) {
  const { isMobile } = useModalContext();
  // Don't let the close control steal focus on press. Without this, clicking
  // Cancel/Close blurs the autofocused form field, flipping it to "touched" and
  // surfacing its validation error as the modal animates out (e.g. clicking
  // Cancel on a pristine form flashes "First name is required"). Radix's own
  // Dialog close button already guards against this — mirror it here. The click
  // (which triggers the close) still fires; keyboard activation is unaffected.
  const handleMouseDown: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    onMouseDown?.(e);
  };
  return isMobile ? (
    <DrawerClose onMouseDown={handleMouseDown} {...props} />
  ) : (
    <DialogClose onMouseDown={handleMouseDown} {...props} />
  );
}

// ---------------------------------------------------------------------------
// Exports
// ---------------------------------------------------------------------------

export {
  Modal,
  ModalClose,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTrigger,
};
